import { create } from 'zustand';
import type { Session, SupabaseClient, User } from '@supabase/supabase-js';
import type { AuthLinkType } from '@cerebro-diem/core';
import type { WebPush } from '../services/createWebPush';
import { errorMessage } from '../services/errors';
import { useFeedbackDialog } from './feedbackStore';

/** The account an email link signed in. */
export interface VerifiedAccount {
  id: string;
  email: string;
}

/** The Supabase auth calls the store makes. */
export type AuthClient = Pick<
  SupabaseClient['auth'],
  'onAuthStateChange' | 'signInWithPassword' | 'signOut' | 'resetPasswordForEmail' | 'verifyOtp' | 'updateUser'
> & {
  /**
   * The session as it stands. Since CER-166 this is the site's own helper,
   * which does not wait on a refresh it does not need, and which throws when a
   * refresh it does need fails rather than reporting an error alongside a
   * session.
   */
  getSession: () => Promise<{ data: { session: Session | null } }>;
};

/** This browser as a notification device: what sign-out turns off (services/webPush.ts). */
export type ThisDevice = Pick<WebPush, 'turnOff'>;

export interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  verifyEmailLink: (tokenHash: string, type: AuthLinkType) => Promise<VerifiedAccount>;
  setPassword: (password: string, accountId: string) => Promise<void>;
}

/** The signed-in user's session, kept in step with `auth`; `device` is this browser's notifications, turned off before the session ends. */
export function createAuthStore(auth: AuthClient, device: ThisDevice) {
  return create<AuthState>((set, get) => {
    /**
     * Show this session. When it belongs to a different user than the one
     * shown, or to nobody, bind feedback to that account. Stored drafts and
     * waiting reports stay with their author through sign-out.
     * Sessions change here on sign-out, and on any auth event: another tab of
     * this site signing out or signing in someone else.
     */
    const showSession = (session: Session | null) => {
      if ((session?.user.id ?? null) !== (get().user?.id ?? null)) {
        void useFeedbackDialog.getState().bindOwner(session?.user.id ?? null).then(() => useFeedbackDialog.getState().retry());
      }
      set({ user: session?.user ?? null, session });
    };

    /** The last sign-out was refused because this device could not be turned off; the next one signs out regardless. */
    let deviceKept = false;

    return {
      user: null,
      session: null,
      isLoading: true,

      initialize: async () => {
        try {
          const { data: { session } } = await auth.getSession();
          showSession(session);
          set({ isLoading: false });

          auth.onAuthStateChange((_event, session) => {
            showSession(session);
          });
        } catch (error) {
          console.error('Auth initialization error:', error);
          set({ isLoading: false });
        }
      },

      signIn: async (email, password) => {
        const { error } = await auth.signInWithPassword({ email, password });
        if (error) throw error;
      },

      signOut: async () => {
        // This browser stops being the account's notification device before
        // the session ends (removing the registration needs it), so nothing of
        // theirs reaches whoever uses the browser next. A refusal is thrown
        // once, so the person knows; signing out again signs out regardless,
        // because a browser that cannot reach the server must still let its
        // user go.
        try {
          await device.turnOff();
          deviceKept = false;
        } catch (error) {
          if (!deviceKept) {
            deviceKept = true;
            throw new Error(
              `notifications could not be turned off on this device (${errorMessage(error)}). Sign out again to sign out anyway; this device may keep showing this account's notifications until they are turned off in Settings.`,
            );
          }
        }
        // When the auth server refuses, supabase-js keeps the browser's session,
        // so the user is still signed in and the page must keep showing them.
        const { error } = await auth.signOut();
        if (error) throw error;
        deviceKept = false;
        showSession(null);
      },

      requestPasswordReset: async (email) => {
        // The reset email (supabase/templates/recovery.html) links to the Site URL's
        // /set-password page, whichever app asked for it.
        const { error } = await auth.resetPasswordForEmail(email);
        if (error) throw error;
      },

      verifyEmailLink: async (tokenHash, type) => {
        const { data, error } = await auth.verifyOtp({ token_hash: tokenHash, type });
        if (error) throw error;
        const { session, user } = data;
        // Go no further unless the link signed in exactly the account it verified.
        if (!session || !user || session.user.id !== user.id || !user.email) {
          throw new Error('the link was accepted but did not sign in the account it belongs to');
        }
        return { id: user.id, email: user.email };
      },

      setPassword: async (password, accountId) => {
        // Sessions are shared by every tab of this site; make sure another tab has
        // not signed in someone else since the link was verified.
        const { data } = await auth.getSession();
        if (data.session?.user.id !== accountId) {
          throw new Error(
            'this browser is now signed in to a different account, so no password was changed. Open the link from your email again.',
          );
        }
        const { error } = await auth.updateUser({ password });
        if (error) throw error;
      },
    };
  });
}
